/* eslint-disable max-len */
import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form, Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import { patchGenerarDeclaracion, postDeclaracionCedular } from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const initialValues = {
  es_declaracion_en_ceros: false,
  total_de_ingresos_obtenidos: 0,
  deducciones_autorizadas_comprobables: 0,
  retenciones_por_terceros: 0,
  impuesto_cedular_a_cargo: 0,
  base_del_pago: 0,
  recargos: 0,
  multa_por_correccion_fiscal: 0,
  actualizacion: 0,
  cantidad_a_cargo: 0,
  cantidad_a_favor: 0,
  total_de_contribuciones: 0,
  impuesto_determinado: 0,
  monto_pagado_con_anterioridad: 0,
};

const FormCedularMensual = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  selectedCargos,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [INPC, setINPC] = useState(0);

  // watchers
  const _impuesto = Form.useWatch('impuesto_cedular_a_cargo', form);
  const importeDeActualizacion = Form.useWatch('actualizacion', form);
  const importeDeRecargos = Form.useWatch('recargos', form);
  const importeDeMultas = Form.useWatch('multa_por_correccion_fiscal', form);
  const totalDeContribuciones = Form.useWatch('total_de_contribuciones', form);
  const montoPagadoConAnterioridad = Form.useWatch('monto_pagado_con_anterioridad', form);

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const _actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });

      form.setFieldsValue({ actualizacion: round(_actualizacion || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  const calcularRecargos = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const _impuestoDeterminado = typeof _impuesto === 'number'
        ? _impuesto
        : parseFloat(_impuesto);

      const _recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: _impuestoDeterminado,
        fecha_de_vencimiento: selectedCargos[0]?.fecha_de_vencimiento,
      });

      form.setFieldsValue({ recargos: round(_recargos || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calcularTotalDeContribuciones = () => {
    const valores = [
      _impuesto,
      importeDeActualizacion,
      importeDeRecargos,
      importeDeMultas,
    ].map((valor) => {
      const valorNumerico = typeof valor === 'number' ? valor : parseFloat(valor);
      return Number.isNaN(valorNumerico) ? 0 : valorNumerico;
    });

    const total = valores.reduce((suma, valor) => suma + valor, 0);

    // form.setFieldsValue({ total_de_contribuciones: round(total)[0] });
    const totalRounded = round(total)[0];
    form.setFieldsValue({
      total_de_contribuciones: totalRounded === 0 ? 0 : totalRounded,
    });
  };

  const calcularCantidadACargo = () => {
    try {
      const totalContribuciones = typeof totalDeContribuciones === 'number'
        ? totalDeContribuciones
        : parseFloat(totalDeContribuciones) || 0;

      const montoPagado = typeof montoPagadoConAnterioridad === 'number'
        ? montoPagadoConAnterioridad
        : parseFloat(montoPagadoConAnterioridad) || 0;

      const diferencia = totalContribuciones - montoPagado;

      /* if (diferencia > 0) {
        form.setFieldsValue({
          cantidad_a_cargo: round(diferencia)[0],
          cantidad_a_favor: 0,
        });
      } else {
        form.setFieldsValue({
          cantidad_a_favor: round(Math.abs(diferencia))[0],
          cantidad_a_cargo: 0,
        });
      } */
      if (diferencia > 0) {
        const cargoRounded = round(diferencia)[0];
        form.setFieldsValue({
          cantidad_a_cargo: cargoRounded === 0 ? 0 : cargoRounded,
          cantidad_a_favor: 0,
        });
      } else {
        const favorRounded = round(Math.abs(diferencia))[0];
        form.setFieldsValue({
          cantidad_a_favor: favorRounded === 0 ? 0 : favorRounded,
          cantidad_a_cargo: 0,
        });
      }
    } catch (error) {
      _logger(error);
      form.setFieldsValue({
        cantidad_a_cargo: 0,
        cantidad_a_favor: 0,
      });
    }
  };

  const calculateINPC = async () => {
    if (selectedCargos.length > 0) {
      const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
      const mes = selectedCargos[0]?.identificador_periodo;
      const _inpc = await getINPC(periodo, mes);
      setINPC(_inpc?.[0] ?? {});
    }
  };

  // Calculamos los recargos
  useEffect(() => {
    const importe = typeof importeDeActualizacion === 'number'
      ? importeDeActualizacion
      : parseFloat(importeDeActualizacion);

    if (!Number.isNaN(importe)) {
      calcularRecargos({ importe });
    }
  }, [importeDeActualizacion, _impuesto]);

  // Calculamos la actualización
  useEffect(() => {
    const importe = typeof _impuesto === 'number'
      ? _impuesto
      : parseFloat(_impuesto);

    if (!Number.isNaN(importe)) {
      calcularActualizacion({ importe });
    }
  }, [_impuesto]);

  // calculamos el total de contribuciones
  useEffect(() => {
    calcularTotalDeContribuciones();
  }, [
    importeDeActualizacion,
    _impuesto,
    importeDeRecargos,
    importeDeMultas,
  ]);

  useEffect(() => {
    calcularCantidadACargo();
  }, [totalDeContribuciones, montoPagadoConAnterioridad]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      calculateINPC();
      form.resetFields();
    }
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
        padron?.empresa?.contribuyente?.razon_social
        || padron?.empresa?.contribuyente?.nombre_completo
        || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria && !isZeroState ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_determinado: 0,
          total_de_ingresos_obtenidos: 0,
          impuesto_cedular_a_cargo: 0,
          retenciones_por_terceros: 0,
          base_del_pago: 0,
          deducciones_autorizadas_comprobables: 0,
          monto_pagado_con_anterioridad: 0,
          actualizacion: 0,
          multa_por_correccion_fiscal: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionCedular(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = async (values) => {
    try {
      const retencion_rounded = parseFloat(round(values.retenciones)).toFixed(2);
      const impuesto_rounded = parseFloat(round(values.impuesto)).toFixed(2);
      const impuesto_cedular_a_cargo = parseFloat(round(values.impuesto_cedular_a_cargo)).toFixed(2);

      // Validación de impuestos y retenciones
      const diferenciaImpuestos = parseFloat(impuesto_rounded) - parseFloat(retencion_rounded);

      if (diferenciaImpuestos < 0 && !isZeroState) {
        await Promise.all([
          form.validateFields(['impuesto_a_cargo']).then(() => {
            form.setFields([{
              name: 'impuesto_a_cargo',
              errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
            }]);
          }),
          form.validateFields(['retenciones']).then(() => {
            form.setFields([{
              name: 'retenciones',
              errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
            }]);
          }),
        ]);
      } else if (diferenciaImpuestos >= 0 && !isZeroState) {
        await Promise.all([
          form.validateFields(['impuesto_a_cargo']).then(() => {
            form.setFields([{
              name: 'impuesto_a_cargo',
              errors: [],
            }]);
          }),
          form.validateFields(['retenciones']).then(() => {
            form.setFields([{
              name: 'retenciones',
              errors: [],
            }]);
          }),
        ]);
      }

      // Validación de impuesto cedular
      if (parseFloat(impuesto_cedular_a_cargo) <= 0) {
        await form.validateFields(['impuesto_cedular_a_cargo']).then(() => {
          setDisableFields(true);
        });
      } else {
        await form.validateFields(['impuesto_cedular_a_cargo']).then(() => {
          setDisableFields(false);
        });
      }

      // Resetear campos si la diferencia es menor o igual a cero
      if (diferenciaImpuestos <= 0 && !isZeroState) {
        form.setFieldsValue({
          actualizacion: 0,
          recargos: 0,
          multa_por_correccion_fiscal: 0,
        });
      }
    } catch (error) {
      _logger(error);
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();

    const actualizacion = parseFloat(values.actualizacion) || 0;
    const recargos = parseFloat(values.recargos) || 0;
    const multas = parseFloat(values.multa_por_correccion_fiscal) || 0;
    const monto_pagado_con_anterioridad = parseFloat(values.monto_pagado_con_anterioridad) || 0;

    const impuesto1 = parseInt(values.total_de_ingresos_obtenidos, 10) || 0;
    const impuesto2 = parseInt(values.deducciones_autorizadas_comprobables, 10) || 0;
    const impuesto3 = parseInt(values.retenciones_por_terceros, 10) || 0;

    // OPERACIONES

    const base_del_pago_total = [
      impuesto2,
      impuesto1,
    ].reduce((a, b) => b - a, 0);

    const total_de_contribuciones = [
      actualizacion,
      recargos,
      multas,
    ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0);

    const base_de_pago = base_del_pago_total < 0 ? 0 : round(+base_del_pago_total)[0];
    const impuesto_determinado = round(base_de_pago * 0.03)[0];

    const impuesto_cedular = [
      impuesto_determinado,
      impuesto3,
    ].reduce((a, b) => a - b);

    const impuesto_cedular_a_cargo_positive = impuesto_cedular < 0 ? 0 : impuesto_cedular;

    /* const total_de_contribuciones_rounded = round(parseFloat(
      parseInt(impuesto_cedular_a_cargo_positive, 10)
      + parseInt(total_de_contribuciones, 10),
    )[0]); */
    const totalCalc = parseInt(impuesto_cedular_a_cargo_positive, 10) + parseInt(total_de_contribuciones, 10);
    const total_de_contribuciones_rounded = round(totalCalc)[0];
    const safeTotal = total_de_contribuciones_rounded === 0 ? 0 : total_de_contribuciones_rounded;

    // const cantidad_a_cargo = parseFloat(total_de_contribuciones_rounded - monto_pagado_con_anterioridad)[0];
    // const cantidad_a_pagar_rounded = round(parseFloat(cantidad_a_cargo)[0]);
    const cantidad_a_cargo_calc = total_de_contribuciones_rounded - monto_pagado_con_anterioridad;
    const cantidad_a_cargo_rounded = round(cantidad_a_cargo_calc)[0];
    const safeCargo = cantidad_a_cargo_rounded === 0 ? 0 : cantidad_a_cargo_rounded;

    form.setFieldsValue({
      base_del_pago: base_de_pago,
      impuesto_determinado,
      total_de_contribuciones: safeTotal,
      impuesto_cedular_a_cargo: round(+impuesto_cedular_a_cargo_positive)[0],
    });

    const isNegative = parseInt(total_de_contribuciones_rounded, 10) < parseInt(monto_pagado_con_anterioridad, 10);

    if (isNegative) {
      const total_a_favor_rounded = round(parseFloat((monto_pagado_con_anterioridad - total_de_contribuciones_rounded)))[0];
      form.setFieldsValue({
        cantidad_a_favor: total_a_favor_rounded,
        cantidad_a_cargo: 0,
      });
    } else {
      /* form.setFieldsValue({
        cantidad_a_cargo: cantidad_a_pagar_rounded,
        cantidad_a_favor: 0,
      }); */
      form.setFieldsValue({
        cantidad_a_cargo: safeCargo,
        cantidad_a_favor: 0,
      });
    }
    validacionesDeOperaciones({
      ...values,
      impuesto_cedular_a_cargo: round(parseFloat(impuesto_cedular_a_cargo_positive))[0],
    });
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              PAGO PROVISIONAL MENSUAL DE IMPUESTO CEDULAR POR LA PRESTACIÓN DE SERVICIOS PROFESIONALES
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>
                  Cancelar
                </Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>
                  Generar
                </Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={initialValues}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col span={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="2" style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Text $accent $bold>II. DETERMINACIÓN DEL IMPUESTO</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de ingresos en el mes</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="prueba"
                              name="total_de_ingresos_obtenidos"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Deducciones autorizadas</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="prueba"
                              name="deducciones_autorizadas_comprobables"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Base del pago</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base_del_pago"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_determinado"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Retenciones por teceros</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="retenciones_por_terceros"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto cedular a cargo</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_cedular_a_cargo"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="2" style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Text $accent $bold>III. PAGO DEL IMPUESTO</Text>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Impuesto cedular a cargo</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_cedular_a_cargo"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.entero,
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Actualización (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableFields} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Recargos (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableFields} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Multas (Monto manual)</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multa_por_correccion_fiscal"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled={disableFields} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Monto pagado con anterioridad (Monto manual)</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a pagar</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormCedularMensual.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  selectedCargos: propTypes.arrayOf({
    fecha_de_vencimiento: propTypes.string,
    importe: propTypes.number,
    identificador_periodo: propTypes.string,
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.string,
    }),
  }),
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
};

FormCedularMensual.defaultProps = {
  isComplementaria: false,
  visible: false,
  selectedCargos: [],
  callbackCancel: () => {},
};

export default FormCedularMensual;
